<!-- 财务流水、门店对账与退款流水展示。数据请求和分页由父页面处理。 -->
<template>
  <section class="finance-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <div class="filter-bar__meta">
          <span v-if="section === 'transactions'">财务流水和门店对账已接入后端分页，列表金额按分展示。</span>
          <span v-else-if="section === 'reconciliation'">
            对账按门店聚合全历史已付款订单：<b>已付款金额</b>为订单实付金额合计（<b>已退款订单仍计入</b>），
            <b>已完结订单金额</b>只统计订单状态为「已完成」的部分，<b>与资金是否已结给门店无关</b>。
          </span>
          <span v-else>退款流水已接入后端分页，列表金额按分展示。</span>
        </div>
      </div>

      <div v-if="section === 'transactions'" v-loading="loadingTransactions" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>流水号</th>
              <th>订单号</th>
              <th>支付渠道</th>
              <th class="is-right">金额</th>
              <th>状态</th>
              <th>交易时间</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in transactionPage?.list ?? []" :key="item.id">
              <td><span class="cell-text">{{ item.transactionNo || item.id }}</span></td>
              <td><span class="cell-text">{{ item.orderNo || '--' }}</span></td>
              <td><span class="cell-text">{{ getPaymentChannelText(item.channel) }}</span></td>
              <td class="is-right">{{ formatAmount(item.amount) }}</td>
              <td><span :class="['status-tag', toneClass(financeTransactionStatusTone(item.status))]">{{ getFinanceTransactionStatusText(item.status) }}</span></td>
              <td>{{ item.createdAt || '--' }}</td>
            </tr>
            <tr v-if="!(transactionPage?.list.length) && !loadingTransactions"><td colspan="6"><el-empty description="暂无交易流水" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'reconciliation'" v-loading="loadingReconciliation" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>门店</th>
              <th class="is-right">订单数</th>
              <th class="is-right">已付款金额</th>
              <th class="is-right">已完结订单金额</th>
              <th class="is-right">差额</th>
              <th>状态</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in reconciliationPage?.list ?? []" :key="item.storeId">
              <td><span class="cell-text">{{ getReconciliationStoreNameText(item) }}</span></td>
              <td class="is-right">{{ item.orderCount }}</td>
              <td class="is-right">{{ formatAmount(item.totalAmount) }}</td>
              <td class="is-right">{{ formatAmount(item.settledAmount) }}</td>
              <td class="is-right">{{ formatAmount(item.diffAmount) }}</td>
              <td><span :class="['status-tag', toneClass(reconciliationStatusTone(item.status))]">{{ getReconciliationStatusText(item.status) }}</span></td>
            </tr>
            <tr v-if="!(reconciliationPage?.list.length) && !loadingReconciliation"><td colspan="6"><el-empty description="暂无对账数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else v-loading="loadingRefunds" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>退款单号</th>
              <th>订单号</th>
              <th>用户 ID</th>
              <th>退款渠道</th>
              <th class="is-right">退款金额</th>
              <th>退款状态</th>
              <th>退款时间</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in refundPage?.list ?? []" :key="item.id">
              <td><span class="cell-text">{{ item.refundNo || item.id }}</span></td>
              <td><span class="cell-text">{{ item.orderNo || item.orderId }}</span></td>
              <td><span class="cell-text">{{ item.userId }}</span></td>
              <td><span class="cell-text">{{ getRefundChannelText(item.channel) }}</span></td>
              <td class="is-right">{{ formatAmount(item.amount) }}</td>
              <td><span :class="['status-tag', toneClass(refundStatusTone(item.status))]">{{ getRefundStatusText(item.status) }}</span></td>
              <td>{{ item.createdAt || '--' }}</td>
            </tr>
            <tr v-if="!(refundPage?.list.length) && !loadingRefunds"><td colspan="7"><el-empty description="暂无退款流水" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="section === 'transactions' && transactionPage" class="table-footer">
        <span>共 {{ transactionPage.total }} 条记录，第 {{ transactionPage.page }}/{{ transactionTotalPages }} 页</span>
        <div>
          <button :disabled="transactionPage.page <= 1" @click="emit('changeTransactionPage', transactionPage.page - 1)">上一页</button>
          <button class="is-active">{{ transactionPage.page }}</button>
          <button :disabled="!transactionPage.hasMore" @click="emit('changeTransactionPage', transactionPage.page + 1)">下一页</button>
        </div>
      </div>

      <div v-else-if="section === 'reconciliation' && reconciliationPage" class="table-footer">
        <span>共 {{ reconciliationPage.total }} 条记录，第 {{ reconciliationPage.page }}/{{ reconciliationTotalPages }} 页</span>
        <div>
          <button :disabled="reconciliationPage.page <= 1" @click="emit('changeReconciliationPage', reconciliationPage.page - 1)">上一页</button>
          <button class="is-active">{{ reconciliationPage.page }}</button>
          <button :disabled="!reconciliationPage.hasMore" @click="emit('changeReconciliationPage', reconciliationPage.page + 1)">下一页</button>
        </div>
      </div>

      <div v-else-if="section === 'refunds' && refundPage" class="table-footer">
        <span>共 {{ refundPage.total }} 条记录，第 {{ refundPage.page }}/{{ refundTotalPages }} 页</span>
        <div>
          <button :disabled="refundPage.page <= 1" @click="emit('changeRefundPage', refundPage.page - 1)">上一页</button>
          <button class="is-active">{{ refundPage.page }}</button>
          <button :disabled="!refundPage.hasMore" @click="emit('changeRefundPage', refundPage.page + 1)">下一页</button>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { toRefs } from 'vue'
import type { RefundStatus } from '@jiaofa/constants'
import type { FinanceTransactionStatus, ReconciliationStatus } from '@/api/finance'
import type { OperationStat } from '@/data/operationDesign'

type TransactionPage = Awaited<ReturnType<typeof import('@/api/finance').fetchFinanceTransactions>>
type ReconciliationPage = Awaited<ReturnType<typeof import('@/api/finance').fetchReconciliation>>
type RefundPage = Awaited<ReturnType<typeof import('@/api/finance').fetchFinanceRefunds>>
type FinanceSection = 'transactions' | 'reconciliation' | 'refunds'
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  section: FinanceSection
  stats: OperationStat[]
  transactionPage?: TransactionPage
  reconciliationPage?: ReconciliationPage
  refundPage?: RefundPage
  loadingTransactions: boolean
  loadingReconciliation: boolean
  loadingRefunds: boolean
  transactionTotalPages: number
  reconciliationTotalPages: number
  refundTotalPages: number
  formatAmount: (amountInFen: number) => string
  getPaymentChannelText: (channel?: string) => string
  getFinanceTransactionStatusText: (status: FinanceTransactionStatus) => string
  getReconciliationStatusText: (status: ReconciliationStatus) => string
  getReconciliationStoreNameText: (item: { storeId: string; storeName?: string }) => string
  getRefundChannelText: (channel?: string) => string
  getRefundStatusText: (status: RefundStatus) => string
  financeTransactionStatusTone: (status: FinanceTransactionStatus) => StatusTone
  reconciliationStatusTone: (status: ReconciliationStatus) => StatusTone
  refundStatusTone: (status: RefundStatus) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  changeTransactionPage: [page: number]
  changeReconciliationPage: [page: number]
  changeRefundPage: [page: number]
}>()

const {
  section,
  stats,
  transactionPage,
  reconciliationPage,
  refundPage,
  loadingTransactions,
  loadingReconciliation,
  loadingRefunds,
  transactionTotalPages,
  reconciliationTotalPages,
  refundTotalPages,
  formatAmount,
  getPaymentChannelText,
  getFinanceTransactionStatusText,
  getReconciliationStatusText,
  getReconciliationStoreNameText,
  getRefundChannelText,
  getRefundStatusText,
  financeTransactionStatusTone,
  reconciliationStatusTone,
  refundStatusTone,
  toneClass,
} = toRefs(props)
</script>

<style scoped lang="scss">
.finance-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-bar__meta { flex: 1; color: var(--jf-color-text-secondary); font-size: 13px; }
.filter-bar__actions { display: flex; gap: 8px; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button { height: 36px; padding: 0 16px; border-radius: 8px; background: var(--jf-color-primary); color: #fff; font-size: 14px; font-weight: 700; }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.table-footer button { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; color: var(--jf-color-text-secondary); }
.table-footer .is-active { border-color: var(--jf-color-primary); background: var(--jf-color-primary); color: #fff; }
.table-footer button:disabled { cursor: not-allowed; opacity: .5; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
